import { useEffect, useState } from "react";
import { tokenStore, fetchMe } from "@/lib/apiClient";

// Sign-in/up lives in the central HikmahVerses login app — bounce there and
// have it send the user (with a gateway token) back to /sso/callback.
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || "http://localhost:3007/login";

// SsoCallback stamps this just before landing in the app. If we're back here
// within seconds, the token we were just given was rejected — bouncing again
// would loop forever, so stop and say so instead.
const LOOP_WINDOW_MS = 20000;
const recentlySignedIn = () => {
  try { return Date.now() - Number(sessionStorage.getItem("hv_sso_cb") || 0) < LOOP_WINDOW_MS; } catch { return false; }
};
const dashboard = (page) => `${new URL(LOGIN_URL).origin}/dashboard?page=${page}`;

export default function ExternalLogin({ mode }) {
  const [looped] = useState(recentlySignedIn);
  // Why the session was refused. The gateway answers 402 when the person's plan doesn't include this app (for example once
  // the free year has ended), and that is not a sign-in failure: say what it is and where to go.
  const [blocked, setBlocked] = useState(null);
  const [checked, setChecked] = useState(!looped);   // false until we know which message to show (no flash of the wrong one)

  useEffect(() => {
    if (looped) {
      let cancelled = false;
      fetchMe()
        .catch((e) => { if (!cancelled && e?.response?.status === 402) setBlocked(e.response.data?.detail || {}); })
        .finally(() => { tokenStore.clear(); if (!cancelled) setChecked(true); }); // the rejected token must not stay behind
      return () => { cancelled = true; };
    }
    const u = new URL(LOGIN_URL);
    u.searchParams.set("redirect", `${window.location.origin}/sso/callback`);
    if (mode === "register") u.searchParams.set("mode", "register");
    window.location.replace(u.toString());
  }, [mode, looped]);

  if (!looped || !checked) return null;

  const retry = () => {
    try { sessionStorage.removeItem("hv_sso_cb"); sessionStorage.removeItem("hv_sso_checked"); } catch { /* ignore */ }
    const u = new URL(LOGIN_URL);
    u.searchParams.set("redirect", `${window.location.origin}/sso/callback`);
    u.searchParams.set("switch", "1"); // show the form, don't auto-sign-in again
    window.location.replace(u.toString());
  };

  if (blocked) {
    const ended = blocked.code === "free_year_ended";
    const btn = { display: "inline-block", padding: "10px 20px", borderRadius: 999, border: 0, background: "#1E3F33", color: "#fff", textDecoration: "none", margin: 4, fontSize: 15 };
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, fontFamily: "system-ui, sans-serif", textAlign: "center" }} data-testid="plan-blocked">
        <div style={{ maxWidth: 440 }}>
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>{ended ? "Your free year has ended" : "Quran Fluency isn’t part of your plan"}</h1>
          <p style={{ color: "#555", marginBottom: 16 }}>
            {ended
              ? "Your progress is saved. Renew your free programmes or choose a package to open Quran Fluency again."
              : blocked.message || "Choose a package that includes Quran Fluency to open it."}
          </p>
          <a href={dashboard("plans")} style={btn} data-testid="plan-blocked-plans">See plans</a>
          <a href={dashboard("apps")} style={{ ...btn, background: "transparent", color: "#1E3F33", textDecoration: "underline" }}>Back to my apps</a>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, fontFamily: "system-ui, sans-serif", textAlign: "center" }}>
      <div style={{ maxWidth: 420 }}>
        <h1 style={{ fontSize: 22, marginBottom: 8 }}>We couldn&apos;t sign you in</h1>
        <p style={{ color: "#555", marginBottom: 16 }}>
          You signed in, but this app didn&apos;t accept the session. Please try again; if it keeps happening, contact support.
        </p>
        <button onClick={retry} style={{ padding: "10px 20px", borderRadius: 999, border: 0, background: "#1E3F33", color: "#fff", cursor: "pointer" }}>
          Try signing in again
        </button>
      </div>
    </div>
  );
}
